import { memo, useCallback } from 'react'

import { Cancel } from '@mui/icons-material'
import { Box, TextField, Typography } from '@mui/material'
import { VideoObjectInsert, VideoStreamObjectInsert } from '@nativewaves/nw-cloud-sdk/content'
import { useQuery } from '@tanstack/react-query'
import { nanoid } from 'nanoid'
import { useSnackbar } from 'notistack'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useShallow } from 'zustand/react/shallow'

import { CodeInline } from '@shared/components/MaterialUIEnhancements/CodeInline'
import { useCloseHandler } from '@shared/components/MaterialUIEnhancements/Dialog'
import {
  selectSubmissionProgressSetters,
  SubmissionButton,
  SubmissionProgress,
  useSubmissionProgressStore,
} from '@shared/components/SubmissionProgress'
import { DialogFormContent } from '@shared/layouts'
import { useVideoStreamObjectCreateMutation } from 'hooks/mutations/content'
import { videoStreamObjectsOverviewRoute } from 'pages/NWPlatform/Workspaces/Workspace/Video/Streams/Stream'
import { workspaceQueryKeys, videoStreamQueryKeys } from 'services/queryKeys'

type FormStructure = VideoObjectInsert | VideoStreamObjectInsert

const NewDialogRaw = () => {
  const { t } = useTranslation(['common', 'domain'])
  const { enqueueSnackbar } = useSnackbar()
  const handleClose = useCloseHandler()

  const { workspaceId, videostreamId: streamId } = videoStreamObjectsOverviewRoute.useParams()

  const { setCurrentStep } = useSubmissionProgressStore(useShallow(selectSubmissionProgressSetters))

  const workspaceQuery = useQuery(workspaceQueryKeys.detail({ workspaceId }))
  const { data: workspace } = workspaceQuery

  const videoStreamQuery = useQuery(videoStreamQueryKeys.detail({ streamId }))
  const { data: videoStream } = videoStreamQuery

  const { mutate: createVideoStreamObject, ...videoStreamObjectCreateProps } = useVideoStreamObjectCreateMutation()

  const { handleSubmit, control } = useForm<FormStructure>({
    defaultValues: {
      repositoryId: workspace?.contentRepositoryId,
    },
  })

  const handleFormSubmit = useCallback(
    (data: FormStructure) => {
      setCurrentStep({ value: 10, description: t('domain:Workspace.Video.creatingVideoObject') })

      createVideoStreamObject(
        {
          streamId: streamId,
          videoStreamObjectInsert: { ...(data as VideoStreamObjectInsert), sessionKey: nanoid(16) },
        },
        {
          onSuccess: (data) => {
            setCurrentStep({
              value: 100,
              description: `${t('domain:Workspace.Video.createdVideoObjectFor')} ${streamId}`,
            })

            setTimeout(() => {
              enqueueSnackbar(`${t('created', { name: data.name })}`, {
                description: `${t('domain:Workspace.Video.Stream.Object.createdDescription')}`,
                variant: 'panel',
                persist: true,
                icon: 'success',
              })

              handleClose()
            }, 700)
          },
          onError: () => {
            setCurrentStep({
              value: 100,
              description: `${t('domain:Workspace.Video.somethingWentWrong')}`,
              icon: <Cancel fontSize="small" color="error" />,
            })
          },
        },
      )
    },
    [setCurrentStep, t, createVideoStreamObject, streamId, enqueueSnackbar, handleClose],
  )

  return (
    <DialogFormContent
      onSubmit={handleSubmit(handleFormSubmit)}
      title={t('domain:Workspace.Video.addVideoObject')}
      description={t('domain:Workspace.Video.videoObjectCreateDescription')}
      SubmitProgressZone={
        <SubmissionProgress
          isSuccess={videoStreamObjectCreateProps.isSuccess}
          isError={videoStreamObjectCreateProps.isError}
        />
      }
      SaveButton={
        <SubmissionButton
          submitCondition={!videoStreamObjectCreateProps.isSuccess && !videoStreamObjectCreateProps.isError}
          successCondition={videoStreamObjectCreateProps.isSuccess}
          errorCondition={videoStreamObjectCreateProps.isError}
          loading={videoStreamObjectCreateProps.isPending}
          disabled={videoStreamObjectCreateProps.isPending || videoStreamObjectCreateProps.isSuccess}
        />
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: 2, my: 2 }}>
        <Controller
          control={control}
          name="name"
          rules={{
            required: true,
          }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label={t('name')}
              required
              helperText={fieldState.error?.message}
              error={!!fieldState.error}
            />
          )}
        />
      </Box>
      <>
        <Typography variant="heading-xs">{t('domain:Workspace.Video.belongsToVideoStream')}</Typography>

        <CodeInline sx={{ my: 0 }} variant="text">
          {videoStream?.name || streamId}
        </CodeInline>
      </>
    </DialogFormContent>
  )
}

export const NewDialog = memo(NewDialogRaw)
